import * as storage from '../../../utils/storage';
import DesignSystemButton from '../../../components/Button/DesignSystemButton';
import Error from '../../../components/Error/Error';
import Loading from '../../../components/Loading/Loading';
import React, { useEffect } from 'react';
import Show from '../../../components/Show/Show';
import { APPLY_STRATEGY } from '../../../state/queries/RecommendationStrategy';
import { COLORS } from '../../../config/designSystem/Colors';
import { ContentSection, ContentSectionItem } from '../../../components/Library/ContentSection';
import { HeaderImageWithCard } from '../../../components/Containers/HeaderImageWithCard/HeaderImageWithCard';
import { SPACING } from '../../../config/designSystem/Spacing';
import { ScrollView, View } from 'react-native';
import { SectionContent } from '../../../components/Assistance/SectionContent';
import { SectionItem } from '../../../components/Assistance/SectionItem';
import { SectionTitle } from '../../../components/Title/SectionTitle';
import { anonymousStackKey, assistanceKey, assistanceStackKey, organizationAssistanceKey, privateKey, publicKey, reckoningIdStorageKey, reckoningResultKey, tabsKey } from '../../../constants/routeKeys';
import { concatPath } from '../../../utils/links/concatPath';
import { loadString } from '../../../utils/storage/storage';
import { observer } from 'mobx-react';
import { redirectStringForStore } from '../../../constants/storageKeys';
import { registerKey } from '../../../constants/routeKeys';
import { useBreakpointWidth } from '../../../hooks/useBreakpointWidth';
import { useFetchedContentArray } from '../../../hooks/content/useFetchedContentArray';
import { useLocalSearchParams, useRouter, useSegments } from 'expo-router';
import { useOnMount } from '../../../hooks/useOnMount';
import { useQuery } from '@apollo/client';
import { useStores } from '../../../hooks/useStores';


type SurveyResultProps = {
  featureId: string;
  strategyId: string;
}

type RecommendedResult = {
  order: number;
  recommendedEntity: string;
  type: string;
  score: number;
}


const SurveyResult: React.FunctionComponent = observer(() => {
  const {language, user, content, feature, reckoning} = useStores();
  const router = useRouter();
  const labels = language?.translate.MOBILE.SURVEY_RESULTS;
  const hasAssistance = user?.company?.hasAccessToEvolunoAssistance || user?.company?.hasAccessToWpoAssistance;
  const ratioInstantHelpImage = 2;
  const segments = useSegments();
  const [reckoningId, setReckoningId] = React.useState('');

  const reckoningData = reckoning.getReckoning(reckoningId);
  const allowRegistration = reckoningData?.allowRegistration;
  const isAnon = segments.includes(anonymousStackKey);

  const MARGIN_LEFT = useBreakpointWidth({lg: SPACING['sp-80'],md: SPACING['sp-40'], sm: SPACING['sp-8']});


  const params = useLocalSearchParams<SurveyResultProps>();

  const { data, loading: queryLoading, error: queryError } = useQuery(APPLY_STRATEGY, {
    variables: {
      strategyId: params.strategyId,
      limit: 10
    },
    fetchPolicy: 'cache-first'
  });

  const results : RecommendedResult[] = data?.applyStrategyById || [];

  const getReckoningId = async () => {
    const reckoningId = await loadString(reckoningIdStorageKey);
    if(!reckoningId) return;
    setReckoningId(reckoningId);
  };

  useEffect(() => {
    // reckoningId arrives asynchronously (getReckoningId reads it from storage on
    // mount), so this used to fire once with it still undefined -- which sent
    // reckoningById(id: null) and came back as the 500 "No ID provided for retrieve
    // actionReckoningService". It also never retried, because reckoningId was not in
    // the dependency list, so the fetch it was meant to do never happened either.
    // Not every survey result comes from a reckoning, so a missing id is normal here.
    if(!reckoningId) return;
    if(!reckoningData) reckoning.fetchReckoningIfNotExist(reckoningId,!user?.user);
  }, [reckoningId, !reckoningData, !user?.user]);

  useEffect(() => {
    if(!!reckoningData && isAnon && allowRegistration) {
      const redirectUrl = `/${reckoningResultKey}?featureId=${reckoningData.resultFeature}&strategyId=${reckoningData.resultRecommendationStrategy}`;
      storage.saveString(redirectStringForStore, redirectUrl);
    }
  }, [isAnon, reckoningData, allowRegistration]);

  useOnMount(() => {
    getReckoningId();
  });

  // Create configs for content fetching
  const contentConfigs = results
    .filter((item: any) => item.type !== 'ASSISTANCE')
    .map(item => ({
      contentId: item.recommendedEntity,
      type: item.type as any
    }));

  const { isLoading: contentLoading, error: contentError, fetch: fetchContent } = useFetchedContentArray(contentConfigs);

  // Fetch content when recommendations are available
  useEffect(() => {
    if (results.length > 0) {
      fetchContent();
    }
  }, [results.length, fetchContent]);

  useOnMount(() => {
    feature?.fetchFeature(params.featureId);
  });

  const goToAssistance = () => {
    router.navigate({
      pathname: concatPath([assistanceStackKey, assistanceKey],1) as string
    });
  };

  const goToRegister = () => {
    router.navigate({
      pathname: concatPath([publicKey, tabsKey, registerKey],-1) as string});
  };

  // Filter and sort recommendations by type
  const assistanceRecommendation = results
    .filter((item: any) => item.type === 'ASSISTANCE');

  const showAssistanceCard = (assistanceRecommendation.length>0  && hasAssistance);


  const companyAssistanceRecommendation = results
    .filter((item: any) => item.type === 'COMPANY_ASSISTANCE');

  const showCompanyAssistanceCard = (companyAssistanceRecommendation.length>0 );

  const showAssistanceSection = (showAssistanceCard || showCompanyAssistanceCard);


  const pathRecommendations: ContentSectionItem[] = results
    .filter((item: any) => item.type === 'PATH')
    .sort((a: any, b: any) => b.order - a.order)
    .slice(0, 3)
    .map((item: any) => ({
      _id: item.recommendedEntity,
      contentType: 'PATH',
      contentId: item.recommendedEntity,
      order: item.order
    }));



  const activityRecommendations: ContentSectionItem[] = results
    .filter((item: any) => !['PATH', 'ASSISTANCE'].includes(item.type))
    .sort((a: any, b: any) => b.order - a.order)
    .slice(0, 5)
    .map((item: any) => {
      const contentItem = content.getContent(item.recommendedEntity);
      let specificContentType = item.type;

      if (item.type === 'CONTENT') {
        specificContentType = contentItem?.type === 'VIDEO' ? 'CONTENT_VIDEO' :
          contentItem?.type === 'AUDIO' ? 'CONTENT_AUDIO' : 'CONTENT_TEXT';
      }

      return {
        _id: item.recommendedEntity,
        contentType: specificContentType,
        contentId: item.recommendedEntity,
        order: item.order,
        showActivityType: true
      };
    });

  if(queryLoading || contentLoading) {
    return <Loading />;
  }
  if(queryError || contentError) {
    return(<Error network={false} labels={language.translate.MOBILE?.ERROR} />);
  }

  const titleAndContent= (title: React.ReactNode, content: React.ReactNode) => {
    return(
      <View style={{rowGap: SPACING['sp-16']}}>
        {title}
        {content}
      </View>
    );
  };

  const onPressInternalRessources = () => {
    router.navigate(concatPath([privateKey, assistanceStackKey, organizationAssistanceKey],-1)); // TODO: implement the same system as for NewAssistance in which it should go to redoAssistanceItem survey under certain conditions
  };


  return (
    <ScrollView style={{backgroundColor:'white'}} contentContainerStyle={{paddingBottom: SPACING['sp-96']}}>
      <HeaderImageWithCard featureId={params.featureId}/>
      <Show when={!isAnon}>
        <View style={{margin: SPACING['sp-16'], rowGap: SPACING['sp-32']}}>

          <Show when={showAssistanceSection}>
            {titleAndContent(
              <SectionTitle title={labels?.ASSISTANCE?.TITLE} subtitle={labels?.ASSISTANCE?.SUBTITLE} displayAll={false}/>,
              <SectionContent>
                <Show when={showAssistanceCard}>
                  <View style={{  alignItems: 'flex-start', marginLeft: MARGIN_LEFT }}>
                    <SectionItem
                      onPress={goToAssistance}
                      text={labels?.ASSISTANCE?.BUTTON}
                      backgroundColor={COLORS.state.info.primary}
                      textColor={COLORS.content['inverse-primary']}
                      nameImage={'assistance_schedule'}
                      positionImage='top'
                      widthImage={279/ratioInstantHelpImage}
                      heightImage={176/ratioInstantHelpImage}
                    />
                  </View>
                </Show>
                <Show when={showCompanyAssistanceCard}>
                  <View >
                    <SectionItem
                      onPress={onPressInternalRessources}
                      text={labels?.ASSISTANCE?.COMPANY_BUTTON}
                      backgroundColor={COLORS.brand[50]}
                      textColor={COLORS.content['accent-dark']}
                      nameImage={'assistance_contacts'}
                      positionImage={'top'}
                      widthImage={80/ratioInstantHelpImage}
                      heightImage={244/ratioInstantHelpImage}
                    />
                  </View>
                </Show>
              </SectionContent>
            )}


          </Show>
          <Show when={pathRecommendations.length > 0}>
            <SectionTitle title={labels?.PATH?.TITLE} subtitle={labels?.PATH?.SUBTITLE}
              displayAll={false}/>
            <ContentSection
              data={pathRecommendations}
              displayButtonBelow={true}
              showAll={false}
              withTitle={false}
            />
          </Show>

          <Show when={activityRecommendations.length > 0}>
            <SectionTitle title={labels?.ACTIVITIES?.TITLE} subtitle={labels?.ACTIVITIES?.SUBTITLE}
              displayAll={false}/>
            <ContentSection
              data={activityRecommendations}
              showAll={false}
            />
          </Show>
        </View>
      </Show>
      <Show when={isAnon && allowRegistration}>
        <View style={{marginTop: SPACING['sp-64'], marginHorizontal: SPACING['sp-32']}}>
          <DesignSystemButton
            onPress={goToRegister}
            label={labels?.LOGIN_TO_SEE_RECOMMENDATION} />
        </View>
      </Show>

    </ScrollView>
  );
});

export default SurveyResult;
